/**
 * First-party marketing and creator attribution capture.
 *
 * Captures referral identifiers (?via=, ?ref=) and UTM parameters upon
 * landing, and persists them into a 60-day first-party cookie (`purebox_attr`)
 * and localStorage (`purebox.attribution.v1`).
 */

import { UserAttributionPayloadSchema, type UserAttributionPayload } from '@purebox/shared';

export const ATTRIBUTION_COOKIE_NAME = 'purebox_attr';
export const ATTRIBUTION_STORAGE_KEY = 'purebox.attribution.v1';
export const ATTRIBUTION_COOKIE_MAX_AGE = 60 * 24 * 60 * 60; // 60 days in seconds

/**
 * Known startup, AI, and SaaS directories that append `?ref=<directory>` to
 * outbound links without `utm_source` / `utm_medium`. Normalizing these into
 * `utmSource` + `utmMedium: 'directory'` ensures GA4 and backend attribution
 * record them as directory traffic rather than burying them in `(direct)`.
 */
const DIRECTORY_REF_SOURCE_MAP: Record<string, string> = {
  producthunt: 'producthunt',
  'producthunt.com': 'producthunt',
  productwatch: 'productwatch',
  'productwatch.io': 'productwatch',
  futuretools: 'futuretools.io',
  'futuretools.io': 'futuretools.io',
  futurepedia: 'futurepedia.io',
  'futurepedia.io': 'futurepedia.io',
  taaft: 'theresanaiforthat.com',
  theresanaiforthat: 'theresanaiforthat.com',
  'theresanaiforthat.com': 'theresanaiforthat.com',
  betalist: 'betalist.com',
  'betalist.com': 'betalist.com',
  alternativeto: 'alternativeto.net',
  'alternativeto.net': 'alternativeto.net',
  saashub: 'saashub.com',
  'saashub.com': 'saashub.com',
  sumodir: 'sumodir.com',
  'sumodir.com': 'sumodir.com',
  aipure: 'aipure.ai',
  'aipure.ai': 'aipure.ai',
  toolify: 'toolify.ai',
  'toolify.ai': 'toolify.ai',
};

export function resolveDirectoryUtmFromRef(
  refValue: string | null | undefined,
): { utmSource: string; utmMedium: 'directory' } | null {
  if (!refValue) return null;
  const normalized = refValue.trim().toLowerCase().replace(/^www\./, '');
  const mappedSource = DIRECTORY_REF_SOURCE_MAP[normalized];
  if (!mappedSource) return null;
  return { utmSource: mappedSource, utmMedium: 'directory' };
}

/**
 * Parses attribution from an arbitrary query string or URL object.
 */
export function extractAttributionFromParams(
  searchParams: URLSearchParams,
  landingUrl?: string,
): UserAttributionPayload | null {
  const via = searchParams.get('via')?.trim() || undefined;
  const ref = searchParams.get('ref')?.trim() || undefined;
  const directoryUtm = resolveDirectoryUtmFromRef(ref);

  const utmSource =
    searchParams.get('utm_source')?.trim() || directoryUtm?.utmSource || undefined;
  const utmMedium =
    searchParams.get('utm_medium')?.trim() || directoryUtm?.utmMedium || undefined;
  const utmCampaign = searchParams.get('utm_campaign')?.trim() || undefined;
  const utmContent = searchParams.get('utm_content')?.trim() || undefined;
  const utmTerm = searchParams.get('utm_term')?.trim() || undefined;

  const referralId = via || ref;

  const hasAnyAttribution = Boolean(
    referralId ||
    utmSource ||
    utmMedium ||
    utmCampaign ||
    utmContent ||
    utmTerm,
  );

  if (!hasAnyAttribution) {
    return null;
  }

  return {
    referralId,
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    utmTerm,
    landingUrl: landingUrl?.slice(0, 500),
  };
}

/**
 * Read the attribution cookie from document.cookie.
 */
export function readAttributionCookie(): UserAttributionPayload | null {
  if (typeof document === 'undefined') return null;
  try {
    const match = document.cookie.match(
      new RegExp('(^|;\\s*)' + ATTRIBUTION_COOKIE_NAME + '=([^;]*)'),
    );
    if (!match?.[2]) return null;
    const decoded = decodeURIComponent(match[2]);
    return UserAttributionPayloadSchema.parse(JSON.parse(decoded));
  } catch {
    return null;
  }
}

/**
 * Write attribution cookie and backup to localStorage.
 */
export function writeAttribution(payload: UserAttributionPayload): void {
  if (typeof window === 'undefined') return;

  try {
    const jsonStr = JSON.stringify(payload);
    const encoded = encodeURIComponent(jsonStr);
    const isSecure = window.location.protocol === 'https:';
    document.cookie = `${ATTRIBUTION_COOKIE_NAME}=${encoded}; path=/; max-age=${ATTRIBUTION_COOKIE_MAX_AGE}; SameSite=Lax${
      isSecure ? '; Secure' : ''
    }`;
  } catch {
    // Cookie write failure ignored
  }

  try {
    window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // LocalStorage write failure ignored
  }
}

/**
 * Resolve stored attribution from cookie or localStorage.
 */
export function getStoredAttribution(): UserAttributionPayload | null {
  const fromCookie = readAttributionCookie();
  if (fromCookie) return fromCookie;

  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    return UserAttributionPayloadSchema.parse(JSON.parse(raw));
  } catch {
    return null;
  }
}

/**
 * Clear stored attribution (for testing or resets).
 */
export function clearStoredAttribution(): void {
  if (typeof window !== 'undefined') {
    try {
      document.cookie = `${ATTRIBUTION_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
      window.localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
    } catch {
      // Ignored
    }
  }
}

/**
 * Capture attribution on page landing.
 *
 * First-touch wins: once a referral is stored, a later visit carrying a
 * different referral must NOT overwrite it. Individual fields are only filled
 * in when the stored record has no value for them.
 */
export function captureAttributionFromUrl(url?: string): UserAttributionPayload | null {
  if (typeof window === 'undefined' && !url) return null;

  try {
    const targetUrl = url ? new URL(url) : new URL(window.location.href);
    const incoming = extractAttributionFromParams(targetUrl.searchParams, targetUrl.href);
    const tracked = incoming;

    if (!tracked) {
      return getStoredAttribution();
    }

    const existing = getStoredAttribution();

    if (!existing) {
      writeAttribution(tracked);
      return tracked;
    }

    // A stored referral identifies the creator we owe. Treat that attribution
    // as immutable and ignore the competing referral entirely, so we never mix
    // creator A's id with creator B's UTMs.
    const hasExistingReferral = Boolean(existing.referralId);
    const hasIncomingReferral = Boolean(tracked.referralId);

    if (hasExistingReferral && hasIncomingReferral) {
      return existing;
    }

    const merged: UserAttributionPayload = {
      referralId: existing.referralId ?? tracked.referralId,
      utmSource: existing.utmSource ?? tracked.utmSource,
      utmMedium: existing.utmMedium ?? tracked.utmMedium,
      utmCampaign: existing.utmCampaign ?? tracked.utmCampaign,
      utmContent: existing.utmContent ?? tracked.utmContent,
      utmTerm: existing.utmTerm ?? tracked.utmTerm,
      landingUrl: existing.landingUrl ?? tracked.landingUrl,
    };

    writeAttribution(merged);
    return merged;
  } catch {
    return null;
  }
}

/**
 * Parse attribution from server-side Cookie header string.
 */
export function parseAttributionFromCookieHeader(
  cookieHeader?: string | null,
): UserAttributionPayload | null {
  if (!cookieHeader) return null;
  try {
    const match = cookieHeader.match(
      new RegExp('(^|;\\s*)' + ATTRIBUTION_COOKIE_NAME + '=([^;]*)'),
    );
    if (!match?.[2]) return null;
    const decoded = decodeURIComponent(match[2]);
    return UserAttributionPayloadSchema.parse(JSON.parse(decoded));
  } catch {
    return null;
  }
}

/**
 * Sync stored attribution to backend /v1/attribution if available.
 */
export async function syncAttributionToServer(
  accessToken: string,
  apiUrl = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3001',
): Promise<boolean> {
  captureAttributionFromUrl();
  const attribution = getStoredAttribution();
  if (!attribution) return false;

  try {
    const res = await fetch(`${apiUrl}/v1/attribution`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(attribution),
    });

    return res.ok;
  } catch {
    return false;
  }
}
